import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkinsApi, type ApiError, UserLevel, SubscriptionLevel } from '../lib/api';
import { type Event, type EventWithRSVP } from '../lib/api/events';
import { useAuth } from '../hooks/useAuth';
import { useClubById } from '../hooks/useClubs';
import { calculateCheckinPermissions, getCheckinOptionDescription, getUpgradeMessage, type CheckinOption } from '../lib/checkinPermissions';
import { getCurrentPosition, calculateDistance, formatDistance } from '../lib/geoUtils';
import { queueCheckin, isOnline } from '../lib/offlineCheckin';
import LocationConsentModal from './LocationConsentModal';

interface CheckinButtonProps {
  event: Event | EventWithRSVP;
  onSuccess?: () => void;
}

type CheckinState = 'idle' | 'requesting-location' | 'submitting' | 'success' | 'error' | 'queued';

export default function CheckinButton({ event, onSuccess }: CheckinButtonProps) {
  const [state, setState] = useState<CheckinState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showOptions, setShowOptions] = useState(false);
  const [showLocationConsent, setShowLocationConsent] = useState(false);
  const [pendingCheckinType, setPendingCheckinType] = useState<CheckinOption | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get club membership information to determine user's role
  const { data: clubData } = useClubById(event.club_id);

  // Calculate permissions for this user and event
  const permissions = useMemo(() => {
    if (!user) return { enabled: false, availableOptions: [], lockedFeatures: [], canCheckinOthers: false };
    
    // Determine user role properly based on club membership
    let userRole: 'leader' | 'member' = 'member'; // Default fallback
    
    if (user.is_platform_admin) {
      // Platform admins get leader permissions in all clubs
      userRole = 'leader';
    } else if (clubData?.membership?.role) {
      // Use actual club membership role
      const clubRole = clubData.membership.role;
      // Map club roles to permission levels (leader gets 'leader' permissions, owner is checked via is_owner)
      userRole = (clubRole === 'leader' || clubData.membership.is_owner) ? 'leader' : 'member';
    }
    // else: defaults to 'member' for users without club membership data
    
    const isEventCreator = event.created_by === user.id;
    
    return calculateCheckinPermissions({
      event,
      userRole: userRole as 'leader' | 'member',
      userLevel: user.user_level as UserLevel,
      clubSubscriptionLevel: user.subscription_level as SubscriptionLevel,
      isEventCreator
    });
  }, [event, user, clubData]);

  const handleCheckinByType = async (type: CheckinOption) => {
    setErrorMessage(null);

    // For GPS check-ins, show location consent modal first
    if (type === 'gps') {
      setPendingCheckinType(type);
      setShowLocationConsent(true);
      return;
    }

    setState('submitting');

    try {
      switch (type) {
        case 'qr':
          // Navigate to QR scanner
          navigate('/scan');
          return;
        case 'manual':
          await handleManualCheckin();
          break;
        case 'photo':
          await handlePhotoCheckin();
          break;
        case 'admin_checkin_others':
          // Navigate to admin check-in interface
          navigate(`/event/${event.id}/admin-checkin`);
          return;
        default:
          throw new Error('Unknown check-in type');
      }
    } catch (err) {
      const apiError = err as ApiError;
      setState('error');
      setErrorMessage(apiError.message || 'Failed to check in');
    }
  };

  const handleGPSCheckin = async () => {
    setState('requesting-location');

    const locationResult = await getCurrentPosition();

    if (!locationResult.success) {
      setState('error');
      setErrorMessage(locationResult.error?.message || 'Failed to get location');
      return;
    }

    const { lat, lng } = locationResult.coordinates!;

    // Validate location if required
    if (event.location_lat && event.location_lng && event.checkin_radius_m) {
      const distance = calculateDistance(
        lat,
        lng,
        event.location_lat,
        event.location_lng
      );

      if (distance > event.checkin_radius_m) {
        setState('error');
        setErrorMessage(
          `You are ${formatDistance(distance)} away from the event location. Must be within ${formatDistance(event.checkin_radius_m)}.`
        );
        return;
      }
    }

    setState('submitting');

    // Handle offline mode
    if (!isOnline()) {
      queueCheckin(event.id, { lat, lng, source: 'offline' });
      setState('queued');
      return;
    }

    await checkinsApi.createCheckin(event.id, { lat, lng, source: 'gps' });
    setState('success');
    onSuccess?.();
  };

  const handleManualCheckin = async () => {
    if (!isOnline()) {
      queueCheckin(event.id, { source: 'offline' });
      setState('queued');
      return;
    }

    await checkinsApi.createCheckin(event.id, { source: 'gps' });
    setState('success');
    onSuccess?.();
  };

  const handlePhotoCheckin = async () => {
    // TODO: Implement photo capture/upload
    // For now, just do a manual check-in
    await handleManualCheckin();
  };

  const resetToIdle = () => {
    setState('idle');
    setErrorMessage(null);
    setShowOptions(false);
    setShowLocationConsent(false);
    setPendingCheckinType(null);
  };

  const handleLocationConsentAccept = async () => {
    setShowLocationConsent(false);
    
    if (pendingCheckinType === 'gps') {
      setState('submitting');
      
      try {
        await handleGPSCheckin();
      } catch (err) {
        const apiError = err as ApiError;
        setState('error');
        setErrorMessage(apiError.message || 'Failed to check in');
      }
    }
    
    setPendingCheckinType(null);
  };

  const handleLocationConsentDecline = () => {
    setShowLocationConsent(false);
    setPendingCheckinType(null);
    setState('idle');
  };

  // If check-ins are disabled
  if (!permissions.enabled) {
    return (
      <div className="bg-surface border border-border/60 rounded-lg p-6">
        <div className="text-center text-text-muted">
          <div className="text-4xl mb-2">🚫</div>
          <p>{permissions.disabledReason}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (state === 'success') {
    return (
      <div className="bg-surface border border-green-500/40 rounded-lg p-6 shadow-glow">
        <div className="text-center">
          <div className="text-6xl mb-4">✓</div>
          <h2 className="text-2xl font-semibold text-green-400 mb-2">Checked In!</h2>
          <p className="text-text-secondary">
            You are checked in to this event.
          </p>
        </div>
      </div>
    );
  }

  // Queued state
  if (state === 'queued') {
    return (
      <div className="bg-surface border border-yellow-500/40 rounded-lg p-6 shadow-glow">
        <div className="text-center">
          <div className="text-6xl mb-4">📱</div>
          <h2 className="text-2xl font-semibold text-yellow-400 mb-2">Queued for Sync</h2>
          <p className="text-text-secondary">
            You're offline. Your check-in will sync when you're back online.
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (state === 'error') {
    return (
      <div className="bg-surface border border-red-500/40 rounded-lg p-6 shadow-glow">
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-red-400 mb-2">Check-in Failed</h2>
            <p className="text-text-secondary">{errorMessage}</p>
          </div>
          <button
            onClick={resetToIdle}
            className="bg-surface-alt text-text-primary border border-border px-6 py-3 rounded-md font-semibold hover:border-accent transition w-full"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const isLoading = state === 'requesting-location' || state === 'submitting';
  const loadingMessage = state === 'requesting-location' 
    ? 'Getting your location...' 
    : 'Checking you in...';

  // Single option (simple mode)
  if (permissions.availableOptions.length === 1) {
    const option = permissions.availableOptions[0];
    const { title, description, icon } = getCheckinOptionDescription(option);

    return (
      <div className="bg-surface border border-accent rounded-lg p-6 shadow-glow">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="text-2xl">{icon}</div>
            <div>
              <h2 className="text-xl font-semibold mb-1">{title}</h2>
              <p className="text-text-secondary">{description}</p>
            </div>
          </div>
          <button
            onClick={() => handleCheckinByType(option)}
            disabled={isLoading}
            className="bg-accent text-black px-6 py-3 rounded-md font-semibold hover:bg-accent-light transition text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? loadingMessage : 'Check In'}
          </button>
        </div>
      </div>
    );
  }

  // Multiple options (show selection)
  if (!showOptions) {
    return (
      <div className="bg-surface border border-accent rounded-lg p-6 shadow-glow">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold mb-1">Check-in Available</h2>
            <p className="text-text-secondary">
              {permissions.availableOptions.length} check-in options available
            </p>
          </div>
          <button
            onClick={() => setShowOptions(true)}
            className="bg-oro-gold text-oro-charcoal px-8 py-4 rounded-xl font-semibold hover:bg-oro-gold-dark oro-transition text-lg shadow-card"
          >
            Choose Method
          </button>
        </div>
      </div>
    );
  }

  // Option selection view
  return (
    <>
    <div className="bg-oro-charcoal-light border border-oro-gold rounded-2xl p-6 shadow-card space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Choose Check-in Method</h2>
        <button
          onClick={() => setShowOptions(false)}
          className="text-text-muted hover:text-text-secondary"
        >
          ✕
        </button>
      </div>

      {/* Available Options */}
      <div className="space-y-3">
        {permissions.availableOptions.map((option) => {
          const { title, description, icon } = getCheckinOptionDescription(option);
          
          return (
            <button
              key={option}
              onClick={() => handleCheckinByType(option)}
              disabled={isLoading}
              className="w-full flex items-center gap-3 p-4 bg-oro-charcoal border border-border-soft rounded-xl hover:border-oro-gold oro-transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="text-xl">{icon}</div>
              <div className="text-left">
                <div className="font-semibold">{title}</div>
                <div className="text-sm text-text-secondary">{description}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Locked Features */}
      {permissions.lockedFeatures.length > 0 && (
        <div className="space-y-3 border-t border-border pt-4">
          <div className="text-sm text-text-muted">
            {getUpgradeMessage(permissions.lockedFeatures)}
          </div>
          {permissions.lockedFeatures.map((option) => {
            const { title, description, icon } = getCheckinOptionDescription(option);
            
            return (
              <div
                key={option}
                className="w-full flex items-center gap-3 p-4 bg-surface border border-border rounded-md opacity-50"
              >
                <div className="text-xl">{icon}</div>
                <div className="text-left">
                  <div className="font-semibold">{title}</div>
                  <div className="text-sm text-text-secondary">{description}</div>
                </div>
                <div className="ml-auto text-xs bg-accent/20 text-accent px-2 py-1 rounded">
                  Premium
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>

    {/* Location Consent Modal */}
    <LocationConsentModal
      isOpen={showLocationConsent}
      onAccept={handleLocationConsentAccept}
      onDecline={handleLocationConsentDecline}
    />
    </>
  );
}
